import { isApiError } from '@pa/api'
import { useCreateClinic, type CreateClinicResponse } from '@pa/api/admin'
import { TempPasswordDialog, toastApiError } from '@pa/staff'
import { Select, useToast } from '@pa/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { useId, useMemo, useState } from 'react'
import { Controller, useForm, type FieldPath } from 'react-hook-form'
import { testSummary } from '../model/connection'
import { API_KEY_RE, EMAIL_RE, messageFor } from '../model/messages'
import { slugProblem, suggestSlug } from '../model/slug'
import { timeZoneOptions } from '../model/timezones'

interface FormValues {
  name: string
  slug: string
  timeZone: string
  adminName: string
  adminEmail: string
  baseUrl: string
  apiKey: string
}

/** API.md issue paths → form fields. */
const FIELD_BY_PATH: Record<string, FieldPath<FormValues>> = {
  name: 'name',
  slug: 'slug',
  timeZone: 'timeZone',
  'firstAdmin.name': 'adminName',
  'firstAdmin.email': 'adminEmail',
  'connection.baseUrl': 'baseUrl',
  'connection.apiKey': 'apiKey',
}

function ErrorText({ id, message }: { id: string; message: string | undefined }) {
  return message ? (
    <span className="err-text" id={id}>
      {message}
    </span>
  ) : null
}

/**
 * S-03 Add clinic (UserFlows S-03, Frontend §7.2; API.md §7.3): name, Clinic ID (immutable), whole-hour time zone
 * (US first), first Clinic Admin, optional PF Keeper connection. Then C-13b for the first admin (or the
 * existing-account toast), then S-04.
 */
export function AddClinicPage() {
  const id = useId()
  const toast = useToast()
  const navigate = useNavigate()
  const zones = useMemo(() => timeZoneOptions(), [])
  const [slugEdited, setSlugEdited] = useState(false)
  const [created, setCreated] = useState<CreateClinicResponse | null>(null)
  // gcTime 0 and reset() on Done: the temporary password doesn't stay in the mutation cache (Frontend §6.5).
  const create = useCreateClinic({ mutation: { gcTime: 0 } })
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { name: '', slug: '', timeZone: '', adminName: '', adminEmail: '', baseUrl: '', apiKey: '' },
  })

  const goToClinic = (clinicId: string) =>
    void navigate({ to: '/clinics/$clinicId/$tab', params: { clinicId, tab: 'summary' } })

  async function onSubmit(v: FormValues) {
    const withConnection = v.baseUrl.trim() !== '' || v.apiKey !== ''
    try {
      const result = await create.mutateAsync({
        data: {
          name: v.name.trim(),
          slug: v.slug.trim(),
          timeZone: v.timeZone,
          firstAdmin: { name: v.adminName.trim(), email: v.adminEmail.trim().toLowerCase() },
          ...(withConnection && { connection: { baseUrl: v.baseUrl.trim(), apiKey: v.apiKey } }),
        },
      })
      if (result.connectionTest) {
        toast(
          result.connectionTest.error === null
            ? testSummary(result.connectionTest)
            : `${testSummary(result.connectionTest)}. The clinic was saved as Not connected.`,
          result.connectionTest.error === null ? 'success' : 'error',
        )
      }
      if (result.firstAdmin.temporaryPassword) {
        setCreated(result)
      } else {
        toast(`${result.firstAdmin.user.name} added. They sign in with their existing password.`, 'info')
        create.reset()
        goToClinic(result.clinic.id)
      }
    } catch (e) {
      if (!isApiError(e)) {
        toastApiError(toast, e)
        return
      }
      if (e.code === 'conflict' && e.details?.reason === 'slug_taken') {
        setError('slug', { message: messageFor('slug_taken') }, { shouldFocus: true })
        return
      }
      if (e.code === 'conflict' && e.details?.reason === 'account_not_addable') {
        setError('adminEmail', { message: messageFor('account_not_addable') }, { shouldFocus: true })
        return
      }
      if (e.code === 'invalid_request' && e.issues?.length) {
        let mapped = false
        for (const issue of e.issues) {
          const field = FIELD_BY_PATH[issue.path.join('.')]
          if (field) {
            setError(field, { message: messageFor(issue.code) })
            mapped = true
          }
        }
        if (mapped) return
      }
      toastApiError(toast, e)
    }
  }

  const fieldIds = (name: keyof FormValues) => ({
    id: `${id}-${name}`,
    'aria-invalid': errors[name] ? true : undefined,
    'aria-describedby': errors[name] ? `${id}-${name}-error` : undefined,
  })

  const nameField = register('name', {
    validate: (value) => (value.trim().length >= 1 && value.trim().length <= 120) || messageFor('name_length'),
    onChange: (e: { target: { value: string } }) => {
      if (!slugEdited) setValue('slug', suggestSlug(e.target.value))
    },
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Add clinic</h1>
          <p>The clinic is created with default settings and its first Clinic Admin.</p>
        </div>
      </div>
      <form className="form-grid" onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <section className="card card-pad form-grid" aria-labelledby={`${id}-clinic-title`}>
          <h2 className="form-section-title" id={`${id}-clinic-title`}>
            Clinic
          </h2>
          <div className="form-grid two">
            <div className="field">
              <label className="label" htmlFor={`${id}-name`}>
                Clinic name<span className="req">*</span>
              </label>
              <input className="input" aria-required {...fieldIds('name')} {...nameField} />
              <ErrorText id={`${id}-name-error`} message={errors.name?.message} />
            </div>
            <div className="field">
              <label className="label" htmlFor={`${id}-slug`}>
                Clinic ID<span className="req">*</span>
              </label>
              <input
                className="input mono"
                aria-required
                {...fieldIds('slug')}
                aria-describedby={errors.slug ? `${id}-slug-error` : `${id}-slug-hint`}
                {...register('slug', {
                  validate: (value) => {
                    const problem = slugProblem(value.trim())
                    return problem === null || messageFor(problem)
                  },
                  onChange: () => {
                    setSlugEdited(true)
                  },
                })}
              />
              {errors.slug ? (
                <ErrorText id={`${id}-slug-error`} message={errors.slug.message} />
              ) : (
                <span className="hint" id={`${id}-slug-hint`}>
                  Used in booking links and the embed code. It can&apos;t be changed later.
                </span>
              )}
            </div>
          </div>
          <div className="field">
            <span className="label" id={`${id}-tz-label`}>
              Time zone<span className="req">*</span>
            </span>
            <Controller
              control={control}
              name="timeZone"
              rules={{ validate: (value) => value !== '' || 'Choose a time zone.' }}
              render={({ field }) => (
                <Select
                  labelledBy={`${id}-tz-label`}
                  value={field.value || null}
                  options={zones}
                  searchable
                  placeholder="Choose a time zone"
                  onChange={field.onChange}
                />
              )}
            />
            <ErrorText id={`${id}-timeZone-error`} message={errors.timeZone?.message} />
          </div>
        </section>

        <section className="card card-pad form-grid" aria-labelledby={`${id}-admin-title`}>
          <div>
            <h2 className="form-section-title" id={`${id}-admin-title`}>
              First Clinic Admin
            </h2>
            <p className="muted">They get a temporary password, shown once, and choose their own at first sign-in.</p>
          </div>
          <div className="form-grid two">
            <div className="field">
              <label className="label" htmlFor={`${id}-adminName`}>
                Name<span className="req">*</span>
              </label>
              <input
                className="input"
                aria-required
                autoComplete="off"
                {...fieldIds('adminName')}
                {...register('adminName', {
                  validate: (value) => value.trim().length >= 1 || messageFor('required'),
                })}
              />
              <ErrorText id={`${id}-adminName-error`} message={errors.adminName?.message} />
            </div>
            <div className="field">
              <label className="label" htmlFor={`${id}-adminEmail`}>
                Email<span className="req">*</span>
              </label>
              <input
                className="input"
                type="email"
                aria-required
                autoComplete="off"
                {...fieldIds('adminEmail')}
                {...register('adminEmail', {
                  validate: (value) => EMAIL_RE.test(value.trim()) || messageFor('email_invalid'),
                })}
              />
              <ErrorText id={`${id}-adminEmail-error`} message={errors.adminEmail?.message} />
            </div>
          </div>
        </section>

        <section className="card card-pad form-grid" aria-labelledby={`${id}-pfk-title`}>
          <div>
            <h2 className="form-section-title" id={`${id}-pfk-title`}>
              PF Keeper connection
            </h2>
            <p className="muted">Optional. You can add it later on the clinic&apos;s Connection tab.</p>
          </div>
          <div className="form-grid two">
            <div className="field">
              <label className="label" htmlFor={`${id}-baseUrl`}>
                PF Keeper address
              </label>
              <input
                className="input mono"
                placeholder="https://10.0.0.5:8443"
                autoComplete="off"
                {...fieldIds('baseUrl')}
                {...register('baseUrl', {
                  validate: (value) =>
                    value.trim() !== '' || getValues('apiKey') === '' || 'Enter the address, or clear the API key.',
                })}
              />
              <ErrorText id={`${id}-baseUrl-error`} message={errors.baseUrl?.message} />
            </div>
            <div className="field">
              <label className="label" htmlFor={`${id}-apiKey`}>
                API key
              </label>
              <input
                className="input mono"
                type="password"
                autoComplete="off"
                {...fieldIds('apiKey')}
                {...register('apiKey', {
                  validate: (value) =>
                    (value === '' && getValues('baseUrl').trim() === '') ||
                    API_KEY_RE.test(value) ||
                    (value === '' ? messageFor('api_key_required') : messageFor('api_key_invalid')),
                })}
              />
              <ErrorText id={`${id}-apiKey-error`} message={errors.apiKey?.message} />
            </div>
          </div>
        </section>

        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create clinic'}
          </button>
          <Link to="/clinics" className="btn btn-ghost">
            Cancel
          </Link>
        </div>
      </form>
      {created?.firstAdmin.temporaryPassword && (
        <TempPasswordDialog
          name={created.firstAdmin.user.name}
          email={created.firstAdmin.user.email}
          password={created.firstAdmin.temporaryPassword}
          onDone={() => {
            const clinicId = created.clinic.id
            setCreated(null)
            create.reset()
            goToClinic(clinicId)
          }}
        />
      )}
    </>
  )
}
